<template>
  <div ref="containerEl" class="w-full notranslate" />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import * as monaco from 'monaco-editor'

import cpp from '@/languages/cpp'
import java from '@/languages/java'
import python from '@/languages/python'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useEditorPageStatePresenter } from '@/presentation/composables/editor/useEditorPageStatePresenter'
import { useFreeSpaceStatePresenter } from '@/presentation/composables/freeSpace/useFreeSpaceStatePresenter'
import { useJudgeStatePresenter } from '@/presentation/composables/judge/useJudgeStatePresenter'
import { programLanguageCodeMap } from '@/util/language/programLangugageCodeMap'

monaco.languages.register({
  id: 'haskell'
})
monaco.languages.register({
  id: 'd'
})
monaco.languages.register({
  id: 'scala'
})
monaco.languages.register({
  id: 'ocaml'
})

monaco.languages.registerDocumentFormattingEditProvider('java', {
  provideDocumentFormattingEdits: (model, options, token) => {
    var text = model.getValue()
    var formattedText = text
      .replace(/if\(/g, 'if (')
      .replace(/}else/g, '} else')
      .replace(/}while/g, '} while')
      .replace(/for\(/g, 'for (')
      .replace(/while\(/g, 'while (')
      .replace(/([a-zA-Z0-9_)>])\{/g, '$1 {')
      .replace(/\)([a-zA-Z0-9_)>])/g, ') $1')
      .replace(/\) \)/g, '))')
      .replace(/,([a-zA-Z0-9])/g, ', $1')
      .replace(/,\[/g, ', [')
      .replace(/,\{/g, ', {')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]]) ([*+\-/%&|^])([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^]) ([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]]) ([*+\-/%&|^])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])= ([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])<<([a-zA-Z0-9_])/g, '$1 << $2')
      .replace(/([a-zA-Z0-9_])>>([a-zA-Z0-9_])/g, '$1 >> $2')
      .replace(/([a-zA-Z0-9_"'])\?([a-zA-Z0-9_"']+):([a-zA-Z0-9_"']+)/g, '$1 ? $2 : $3')
      .replace(/([a-zA-Z0-9_"'\]])=([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]]) =([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]])= ([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_\]])([=!<>])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]]) ([=!<>])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]])([=!<>])= ([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])<([a-zA-Z0-9_]+)(?!>)/g, '$1 < $2')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+),/g, '$1<$2,')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)>/g, '$1<$2>')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)</g, '$1<$2<')
      .replace(/(?!<)([a-zA-Z0-9_]+)>([a-zA-Z0-9_])/g, '$1 > $2')
      .replace(/<([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, '<$1> $2')
      .replace(/, ([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, ', $1> $2')
      .replace(/<([a-zA-Z0-9_]+) >> ([a-zA-Z0-9_])/g, '<$1>> $2')
      .replace(/([a-zA-Z0-9_])&&([a-zA-Z0-9_])/g, '$1 && $2')
      .replace(/([a-zA-Z0-9_])\|\|([a-zA-Z0-9_])/g, '$1 || $2')
      .replace(/([a-zA-Z0-9_]);([a-zA-Z0-9_])/g, '$1; $2')

    formattedText = formatIndent(formattedText)

    return [
      {
        range: model.getFullModelRange(),
        text: formattedText
      }
    ]
  }
})

monaco.languages.registerDocumentFormattingEditProvider('python', {
  provideDocumentFormattingEdits: (model, options, token) => {
    var text = model.getValue()
    var formattedText = text
      .replace(/([a-zA-Z0-9_)>])\{/g, '$1 {')
      .replace(/,([a-zA-Z0-9])/g, ', $1')
      .replace(/,\[/g, ', [')
      .replace(/,\{/g, ', {')
      .replace(/([a-zA-Z0-9_])([*+\-/%&|^@])([a-zA-Z0-9_])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_])([*+\-/%&|^@]) ([a-zA-Z0-9_])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_]) ([*+\-/%&|^@])([a-zA-Z0-9_])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_])\/\/([a-zA-Z0-9_])/g, '$1 // $2')
      .replace(/([a-zA-Z0-9_])\*\*([a-zA-Z0-9_])/g, '$1 ** $2')
      .replace(/([a-zA-Z0-9_])<<([a-zA-Z0-9_])/g, '$1 << $2')
      .replace(/([a-zA-Z0-9_])>>([a-zA-Z0-9_])/g, '$1 >> $2')
      .replace(/([a-zA-Z0-9_])([*+\-/%&|^])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_]) ([*+\-/%&|^])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])([*+\-/%&|^])= ([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])\/\/=([a-zA-Z0-9_])/g, '$1 //= $2')
      .replace(/([a-zA-Z0-9_])\*\*=([a-zA-Z0-9_])/g, '$1 **= $2')
      .replace(/([a-zA-Z0-9_])<<=([a-zA-Z0-9_])/g, '$1 <<= $2')
      .replace(/([a-zA-Z0-9_])>>=([a-zA-Z0-9_])/g, '$1 >>= $2')
      .replace(/([a-zA-Z0-9_]):=([a-zA-Z0-9_])/g, '$1 := $2')
      .replace(/([a-zA-Z0-9_"])\?([a-zA-Z0-9_"]+):([a-zA-Z0-9_"]+)/g, '$1 ? $2 : $3')
      .replace(/([a-zA-Z0-9_"'\]])=([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]]) =([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]])= ([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_])([=!<>])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_]) ([=!<>])=([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])([=!<>])= ([a-zA-Z0-9_])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])<([a-zA-Z0-9_]+)(?!>)/g, '$1 < $2')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+),/g, '$1<$2,')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)>/g, '$1<$2>')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)</g, '$1<$2<')
      .replace(/(?!<)([a-zA-Z0-9_]+)>([a-zA-Z0-9_])/g, '$1 > $2')
      .replace(/<([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, '<$1> $2')
      .replace(/, ([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, ', $1> $2')
      .replace(/([a-zA-Z0-9_])&&([a-zA-Z0-9_])/g, '$1 && $2')
      .replace(/([a-zA-Z0-9_])\|\|([a-zA-Z0-9_])/g, '$1 || $2')

    return [
      {
        range: model.getFullModelRange(),
        text: formattedText
      }
    ]
  }
})

monaco.languages.registerDocumentFormattingEditProvider('cpp', {
  provideDocumentFormattingEdits: (model, options, token) => {
    var text = model.getValue()
    var formattedText = text
      .replace(/if\(/g, 'if (')
      .replace(/}else/g, '} else')
      .replace(/}while/g, '} while')
      .replace(/for\(/g, 'for (')
      .replace(/while\(/g, 'while (')
      .replace(/([a-zA-Z0-9_)>])\{/g, '$1 {')
      .replace(/\)([a-zA-Z0-9_)>])/g, ') $1')
      .replace(/\) \)/g, '))')
      .replace(/,([a-zA-Z0-9])/g, ', $1')
      .replace(/,\[/g, ', [')
      .replace(/,\{/g, ', {')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]]) ([*+\-/%&|^])([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^]) ([a-zA-Z0-9_"'])/g, '$1 $2 $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]]) ([*+\-/%&|^])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]])([*+\-/%&|^])= ([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/bits \/ std/g, 'bits/std')
      .replace(/([eE]) - ([0-9])/g, '$1-$2')
      .replace(/([a-zA-Z0-9_"])<<([a-zA-Z0-9_"])/g, '$1 << $2')
      .replace(/([a-zA-Z0-9_"])>>([a-zA-Z0-9_"])/g, '$1 >> $2')
      .replace(/([a-zA-Z0-9_"])\?([a-zA-Z0-9_"]+):([a-zA-Z0-9_"]+)/g, '$1 ? $2 : $3')
      .replace(/([a-zA-Z0-9_"'\]])=([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]]) =([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_"'\]])= ([{a-zA-Z0-9_"'])/g, '$1 = $2')
      .replace(/([a-zA-Z0-9_\]"'])([=!<>])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]"']) ([=!<>])=([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_\]"'])([=!<>])= ([a-zA-Z0-9_"'])/g, '$1 $2= $3')
      .replace(/([a-zA-Z0-9_])<([a-zA-Z0-9_]+)(?!>)/g, '$1 < $2')
      .replace(/include < ([a-zA-Z0-9_])/g, 'include<$1')
      .replace(/template < ([a-zA-Z0-9_])/g, 'template<$1')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+),/g, '$1<$2,')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)>/g, '$1<$2>')
      .replace(/([a-zA-Z0-9_]) < ([a-zA-Z0-9_]+)</g, '$1<$2<')
      .replace(/(?!<)([a-zA-Z0-9_]+)>([a-zA-Z0-9_])/g, '$1 > $2')
      .replace(/<([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, '<$1> $2')
      .replace(/<([a-zA-Z0-9_]+) >> ([a-zA-Z0-9_])/g, '<$1>> $2')
      .replace(/, ([a-zA-Z0-9_]+) > ([a-zA-Z0-9_])/g, ', $1> $2')
      .replace(/([a-zA-Z0-9_])&&([a-zA-Z0-9_])/g, '$1 && $2')
      .replace(/([a-zA-Z0-9_])\|\|([a-zA-Z0-9_])/g, '$1 || $2')
      .replace(/([a-zA-Z0-9_]);([a-zA-Z0-9_])/g, '$1; $2')

    formattedText = formatIndent(formattedText)

    return [
      {
        range: model.getFullModelRange(),
        text: formattedText
      }
    ]
  }
})

const formatIndent = (text: string) => {
  var lines = text.split('\n')
  var indentLevel = 0
  for (var i = 0; i < lines.length; i++) {
    var openBrackets = (lines[i].match(/{/g) || []).length
    var closeBrackets = (lines[i].match(/}/g) || []).length

    indentLevel -= closeBrackets
    lines[i] = '\t'.repeat(Math.max(indentLevel, 0)) + lines[i].trim()
    indentLevel += openBrackets
  }
  return lines.join('\n')
}

const addSuggestions = (
  suggestions: any[],
  list: string[],
  kind: monaco.languages.CompletionItemKind,
  model: monaco.editor.ITextModel,
  position: monaco.Position
) => {
  const lineContent = model.getLineContent(position.lineNumber)
  const wordAtPosition = model.getWordAtPosition(position)
  const wordStartColumn = wordAtPosition ? wordAtPosition.startColumn : position.column
  const wordEndColumn = wordAtPosition ? wordAtPosition.endColumn : position.column
  const currentWord = wordAtPosition ? wordAtPosition.word : ''

  for (let i = 0; i < list.length; i++) {
    const keyword = list[i]
    if (currentWord.length > 0 && keyword.startsWith(currentWord)) {
      suggestions.push({
        label: keyword,
        kind: kind,
        insertText: keyword,
        range: new monaco.Range(position.lineNumber, wordStartColumn, position.lineNumber, wordEndColumn)
      })
    }
  }
}

const javaSuggestions = (model: monaco.editor.ITextModel, position: monaco.Position) => {
  const suggestions: any[] = []

  addSuggestions(suggestions, java.keywords, monaco.languages.CompletionItemKind.Keyword, model, position)
  addSuggestions(suggestions, java.classes, monaco.languages.CompletionItemKind.Class, model, position)
  addSuggestions(suggestions, java.fields, monaco.languages.CompletionItemKind.Field, model, position)
  addSuggestions(suggestions, java.functions, monaco.languages.CompletionItemKind.Function, model, position)

  return suggestions
}

const pythonSuggestions = (model: monaco.editor.ITextModel, position: monaco.Position) => {
  const suggestions: any[] = []

  addSuggestions(suggestions, python.keywords, monaco.languages.CompletionItemKind.Keyword, model, position)
  addSuggestions(suggestions, python.classes, monaco.languages.CompletionItemKind.Module, model, position)
  addSuggestions(suggestions, python.functions, monaco.languages.CompletionItemKind.Function, model, position)

  return suggestions
}

const cppSuggestions = (model: monaco.editor.ITextModel, position: monaco.Position) => {
  const suggestions: any[] = []

  addSuggestions(suggestions, cpp.keywords, monaco.languages.CompletionItemKind.Keyword, model, position)
  addSuggestions(suggestions, cpp.classes, monaco.languages.CompletionItemKind.Class, model, position)
  addSuggestions(suggestions, cpp.objects, monaco.languages.CompletionItemKind.Field, model, position)
  addSuggestions(suggestions, cpp.functions, monaco.languages.CompletionItemKind.Function, model, position)

  return suggestions
}

monaco.languages.registerCompletionItemProvider('java', {
  provideCompletionItems: (model, position) => {
    return { suggestions: javaSuggestions(model, position) }
  }
})

monaco.languages.registerCompletionItemProvider('python', {
  provideCompletionItems: (model, position) => {
    return { suggestions: pythonSuggestions(model, position) }
  }
})

monaco.languages.registerCompletionItemProvider('cpp', {
  provideCompletionItems: (model, position) => {
    return { suggestions: cppSuggestions(model, position) }
  }
})

export default defineComponent({
  model: {
    event: 'change'
  },
  setup(props, ctx) {
    let editor: monaco.editor.IStandaloneCodeEditor
    /**
     * Variables
     */
    const containerEl = ref<HTMLDivElement | null>(null)
    let windowWidth = ref(window.innerWidth)
    let windowHeight = ref(window.innerHeight)

    const { editorFontSize, programLanguage, isLightMode, viewMode } = useConfigStatePresenter()
    const { sourceCode, setSourceCode } = useFreeSpaceStatePresenter()
    const { errorMessage } = useEditorPageStatePresenter()

    /**
     * Methods
     */
    const initMonaco = () => {
      if (containerEl.value) {
        editor = monaco.editor.create(containerEl.value, {
          value: sourceCode.value,
          language: programLanguageCodeMap[programLanguage.value].code,
          theme: isLightMode() ? 'vs' : 'vs-dark',
          automaticLayout: true,
          scrollBeyondLastLine: false,
          minimap: { enabled: false }
        })
      }

      setFontSize(editorFontSize.value)
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
        editor.getAction('editor.action.formatDocument')!.run()
      })
      editor.addCommand(monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
        editor.getAction('editor.action.formatDocument')!.run()
      })

      editorDidMount()
    }
    const editorDidMount = () => {
      editor.onDidChangeModelContent((event: monaco.editor.IModelContentChangedEvent) => {
        const value = editor.getValue()
        removeErrorDecoration()
        setSourceCode(value)
      })
      ctx.emit('editorDidMount', editor)
    }
    const destroyMonaco = () => {
      if (typeof editor !== 'undefined') {
        editor.dispose()
      }
    }

    const extractError = (input: string): string[] => {
      let result: string[] = []
      let regex = /rep\/(.*?)(\r\n|\r|\n)/g
      let match: RegExpExecArray | null

      while ((match = regex.exec(input)) !== null) {
        result.push(match[1])
      }

      return result
    }

    const removeErrorDecoration = () => {
      monaco.editor.setModelMarkers(editor.getModel()!!, 'owner', [])
    }

    const getErrorRegex = (lang: string) => {
      if (lang == 'java') return /java:(\d+): error:/
      else if (lang == 'python') return /line (\d+)/
      else if (lang == 'cpp') return /cpp:(\d+):/
      else return null
    }

    const setErrorDecoration = () => {
      removeErrorDecoration()

      const errorMsgs = extractError(errorMessage.value ?? '')

      // コンパイルエラーメッセージをパースする正規表現
      const regex = getErrorRegex(programLanguage.value)

      if (regex == null) return

      const errorWaves: {
        startLineNumber: number
        startColumn: number
        endLineNumber: number
        endColumn: number
        message: string
        severity: monaco.MarkerSeverity
      }[] = []

      // 各エラーメッセージに対してハイライトを追加
      errorMsgs.forEach((errorMsg: string) => {
        const match = regex.exec(errorMsg)
        if (match !== null) {
          // エラーメッセージから行番号を抽出
          const lineNumber = parseInt(match[1])

          const wave = {
            startLineNumber: lineNumber,
            startColumn: 1,
            endLineNumber: lineNumber,
            endColumn: 65535,
            message: errorMsg,
            severity: monaco.MarkerSeverity.Error
          }

          errorWaves.push(wave)
        } else {
          console.warn('Unable to parse error message: ' + errorMsg)
        }
      })

      // エラーメッセージをmonacoエディタにセット
      monaco.editor.setModelMarkers(editor.getModel()!!, 'owner', errorWaves)
    }

    const setFontSize = (size: number) => {
      let options = { fontSize: size }
      editor.updateOptions(options)
    }

    const { isTestLoading } = useJudgeStatePresenter()

    /**
     * Watch
     */
    watch(
      () => viewMode.value,
      () => {
        destroyMonaco()
        initMonaco()
      }
    )

    watch(
      () => isTestLoading.value,
      () => {
        if (!isTestLoading.value) setErrorDecoration()
      }
    )

    watch(
      () => editorFontSize.value,
      () => {
        setFontSize(editorFontSize.value)
      }
    )

    watch(
      () => sourceCode.value,
      newValue => {
        if (editor) {
          if (newValue !== editor.getValue()) {
            editor.setValue(newValue)
          }
        }
      }
    )

    watch(
      () => programLanguage.value,
      newLanguage => {
        if (editor) {
          const model = editor.getModel()
          if (model && newLanguage) {
            monaco.editor.setModelLanguage(model, programLanguageCodeMap[newLanguage].code)
          }
        }
      }
    )

    /**
     * Life Cycle methods
     */
    onMounted(() => {
      initMonaco()
    })
    onBeforeUnmount(() => {
      destroyMonaco()
    })

    return {
      containerEl
    }
  }
})
</script>

<style lang="postcss">
.errorDecoration {
  background-color: #fc5554;
  margin: 0px 10px;
}
</style>
